import type { AccountDto, TransactionDto } from '@koverta/shared';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  useSyncExternalStore,
  type CSSProperties,
  type ReactNode,
} from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router';
import { AddAccountDialog } from '../pages/AddAccount';
import { TransactionDialog } from '../pages/AddTransaction';
import { Avatar, Avatars, CurrencyChip, Icon, LogoMark, type IconName } from '../components/ui';
import { useHousehold } from '../lib/household';
import { useIsMobile, useOnline, useSidebarWidth } from '../lib/hooks';
import { outbox } from '../lib/outbox';
import { useAccounts, useInbox, useRates } from '../lib/queries';
import '../styles/loans.css';

interface AddOpts {
  accountId?: string;
  tx?: TransactionDto;
  mode?: 'out' | 'in' | 'xfer';
}
interface ShellCtx {
  addTransaction: (opts?: AddOpts) => void;
  addAccount: () => void;
}
const Ctx = createContext<ShellCtx>({ addTransaction: () => {}, addAccount: () => {} });
export const useShell = () => useContext(Ctx);

export function Shell({ children }: { children: ReactNode }) {
  const mobile = useIsMobile();
  const navigate = useNavigate();
  const [txDialog, setTxDialog] = useState<AddOpts | null>(null);
  const [acctDialog, setAcctDialog] = useState(false);
  const addTransaction = useCallback(
    (opts: AddOpts = {}) => {
      if (mobile && !opts.tx) {
        const p = new URLSearchParams();
        if (opts.accountId) p.set('account', opts.accountId);
        if (opts.mode) p.set('mode', opts.mode);
        navigate(`/add${p.size ? `?${p}` : ''}`);
      } else setTxDialog(opts);
    },
    [mobile, navigate],
  );
  const value = { addTransaction, addAccount: () => setAcctDialog(true) };
  return (
    <Ctx.Provider value={value}>
      {mobile ? <MobileShell>{children}</MobileShell> : <DesktopShell>{children}</DesktopShell>}
      {txDialog ? <TransactionDialog {...txDialog} onClose={() => setTxDialog(null)} /> : null}
      {acctDialog ? <AddAccountDialog onClose={() => setAcctDialog(false)} /> : null}
    </Ctx.Provider>
  );
}

function SyncBanner() {
  const online = useOnline();
  const queued = useSyncExternalStore(outbox.subscribe, outbox.get).length;
  if (online && !queued) return null;
  return (
    <div className={`banner ${online ? 'info' : 'warn'}`} role="status">
      <Icon name="bolt" size={15} />
      {online
        ? `Syncing ${queued} offline change${queued === 1 ? '' : 's'}…`
        : `You're offline. ${queued ? `${queued} change${queued === 1 ? '' : 's'} will sync when you reconnect.` : 'Showing the last saved data.'}`}
    </div>
  );
}

function AccountRow({ a }: { a: AccountDto }) {
  const { fmt } = useHousehold();
  return (
    <NavLink
      to={`/accounts/${a.id}`}
      className={({ isActive }) => `acct ${isActive ? 'on' : ''} ${a.closed ? 'closed' : ''}`}
    >
      <span className="an">
        <CurrencyChip currency={a.currency} sm />
        <span className="nm">{a.name}</span>
      </span>
      <span className="ab">{a.currency === 'EUR' ? fmt.money(a.working, 'EUR') : fmt.num(a.working)}</span>
      {a.currency !== fmt.base ? (
        <span className="sub">
          ≈ {fmt.num(a.workingBase)} {fmt.base}
        </span>
      ) : null}
    </NavLink>
  );
}

/** Drag (or arrow-key) handle on the sidebar's right edge. Double-click resets to the default width. */
function SidebarResizer({
  width,
  min,
  max,
  setWidth,
  reset,
}: Omit<ReturnType<typeof useSidebarWidth>, 'content'>) {
  const [dragging, setDragging] = useState(false);
  const handle = useRef<HTMLDivElement>(null);
  useEffect(() => {
    document.body.classList.toggle('side-resizing', dragging);
    return () => document.body.classList.remove('side-resizing');
  }, [dragging]);
  return (
    <div
      ref={handle}
      className={`side-resizer ${dragging ? 'on' : ''}`}
      role="separator"
      aria-orientation="vertical"
      aria-label="Resize sidebar"
      aria-valuenow={width}
      aria-valuemin={min}
      aria-valuemax={max}
      tabIndex={0}
      onPointerDown={(e) => {
        if (e.button !== 0) return;
        e.preventDefault();
        handle.current?.setPointerCapture(e.pointerId);
        setDragging(true);
      }}
      onPointerMove={(e) => {
        if (dragging) setWidth(e.clientX);
      }}
      onPointerUp={() => setDragging(false)}
      onPointerCancel={() => setDragging(false)}
      onDoubleClick={reset}
      onKeyDown={(e) => {
        const step = e.shiftKey ? 40 : 10;
        if (e.key === 'ArrowLeft') setWidth(width - step);
        else if (e.key === 'ArrowRight') setWidth(width + step);
        else if (e.key === 'Home') setWidth(min);
        else if (e.key === 'End') setWidth(max);
        else if (e.key === 'Enter') reset();
        else return;
        e.preventDefault();
      }}
    />
  );
}

function DesktopShell({ children }: { children: ReactNode }) {
  const sidebar = useSidebarWidth();
  const { household, fmt } = useHousehold();
  const { addAccount } = useShell();
  const accounts = useAccounts().data ?? [];
  const inboxCount = useInbox().data?.items.length ?? 0;
  const rate = useRates().data?.current;
  const open = accounts.filter((a) => !a.closed);
  const budget = open.filter((a) => a.group === 'budget');
  const tracking = open.filter((a) => a.group === 'tracking');
  const debts = open.filter((a) => a.group === 'debt');
  const sum = (list: AccountDto[]) => list.reduce((s, a) => s + a.workingBase, 0);
  const nav: [string, string, IconName][] = [
    ['/', 'Home', 'home'],
    ['/plan', 'Plan', 'plan'],
    ['/inbox', 'Inbox', 'inbox'],
    ['/accounts/all', 'All accounts', 'wallet'],
  ];
  return (
    <div
      className={`frame desk ${sidebar.content <= 932 ? 'compact' : ''} ${sidebar.content <= 852 ? 'tight' : ''}`}
      style={{ '--side-w': `${sidebar.width}px` } as CSSProperties}
    >
      <aside className="side" aria-label="Main navigation">
        <div className="brandmark">
          <span className="logo">
            <LogoMark />
          </span>
          <div>
            <b>Ovce na broju</b>
            <small>{household.name}</small>
          </div>
        </div>
        <nav className="nav">
          {nav.map(([to, label, icon]) => (
            <NavLink key={to} to={to} end={to === '/'} className={({ isActive }) => (isActive ? 'on' : '')}>
              <Icon name={icon} />
              {label}
              {to === '/inbox' && inboxCount ? <span className="badge">{inboxCount}</span> : null}
            </NavLink>
          ))}
        </nav>
        <div className="acct-h">
          <span>Budget</span>
          <span>{fmt.num(sum(budget))}</span>
        </div>
        {budget.map((a) => (
          <AccountRow key={a.id} a={a} />
        ))}
        {tracking.length ? (
          <>
            <div className="acct-h">
              <span>Tracking</span>
              <span>{fmt.num(sum(tracking))}</span>
            </div>
            {tracking.map((a) => (
              <AccountRow key={a.id} a={a} />
            ))}
          </>
        ) : null}
        {debts.length ? (
          <>
            <div className="acct-h">
              <span>Loans & debts</span>
              <span>{fmt.num(sum(debts))}</span>
            </div>
            {debts.map((a) => (
              <AccountRow key={a.id} a={a} />
            ))}
          </>
        ) : null}
        {tracking.length || debts.length ? (
          <div className="acct-nw" data-testid="sidebar-net-worth">
            <span>Net worth</span>
            <b>{fmt.num(sum(open))}</b>
          </div>
        ) : null}
        <button className="add-acct" onClick={addAccount}>
          + Add account
        </button>
        <nav className="nav2">
          {(
            [
              ['/reflect', 'Reflect', 'reflect'],
              ['/predictions', 'Predictions', 'forecast'],
              ['/settings', 'Settings', 'settings'],
            ] as const
          ).map(([to, label, icon]) => (
            <NavLink
              key={to}
              to={to}
              className={({ isActive }) => (isActive ? 'on' : '')}
              style={({ isActive }) =>
                isActive ? { background: 'var(--brand-soft)', color: 'var(--brand)' } : undefined
              }
            >
              <Icon name={icon} />
              {label}
            </NavLink>
          ))}
        </nav>
        <div className="side-foot">
          <Avatars />
          <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {household.members.map((m) => m.displayName).join(' & ')}
          </span>
          {rate ? (
            <span
              className="rate"
              title={`Exchange rate ${rate.source === 'manual' ? '(manual override)' : '(NBS middle rate)'}, ${fmt.date(rate.date)}`}
            >
              €1 = {fmt.rate(rate.rate)}
            </span>
          ) : null}
        </div>
      </aside>
      <SidebarResizer {...sidebar} />
      <main className="main" id="main">
        <SyncBanner />
        {children}
      </main>
    </div>
  );
}

function MobileShell({ children }: { children: ReactNode }) {
  const inboxCount = useInbox().data?.items.length ?? 0;
  const loc = useLocation();
  const navigate = useNavigate();
  const tabs: [string, string, IconName][] = [
    ['/', 'Home', 'home'],
    ['/plan', 'Plan', 'plan'],
    ['/add', '', 'plus'],
    ['/inbox', 'Inbox', 'inbox'],
    ['/accounts', 'Accounts', 'wallet'],
  ];
  const active = (to: string) => (to === '/' ? loc.pathname === '/' : loc.pathname.startsWith(to));
  return (
    <div className="frame phone">
      <div className="m-body" id="main">
        <SyncBanner />
        {children}
      </div>
      <nav className="tabbar" aria-label="Main navigation">
        {tabs.map(([to, label, icon]) =>
          to === '/add' ? (
            <div className="tab" key={to}>
              <button className="fab" aria-label="Add transaction" onClick={() => navigate('/add')}>
                <Icon name="plus" size={24} stroke={2.2} />
              </button>
            </div>
          ) : (
            <NavLink key={to} to={to} end={to === '/'} className={`tab ${active(to) ? 'on' : ''}`}>
              <Icon name={icon} size={22} />
              {label}
              {to === '/inbox' && inboxCount ? <span className="badge">{inboxCount}</span> : null}
            </NavLink>
          ),
        )}
      </nav>
    </div>
  );
}

export function MemberBadge({ id }: { id: string }) {
  return <Avatar id={id} />;
}
